import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLock, faArrowTrendDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useMockData } from '../state/MockDataContext';
import { useUpgradeModal } from './UpgradeModal';
import useCnGoodLabel from '../state/useCnGoodLabel';
import {
  isDirectBasis,
  totalSee,
  estimatedSefa,
  DEFAULT_CERTIFICATE_PRICE,
} from '../state/mockData';
import { toFixedNonZero } from 'helpers/numberFormat';

const formatCurrency = (v) =>
  new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(v);

// Data-driven SEFA upsell — supplier-side port of the public portal's
// CbamCostEstimator: two comparison cards ("Today" vs "With SEFA") plus a
// green savings strip, computed from the user's OWN emissions record instead
// of generic marketing copy. Pass `dismissKey` to make it closable (persists).
export default function SefaSavingsCard({ factor, dismissKey, title }) {
  const { t } = useTranslation('freeSupplier');
  const { data, dismissUpsell } = useMockData();
  const { openUpgrade } = useUpgradeModal();
  const cnGoodLabel = useCnGoodLabel();

  if (!factor) return null;
  if (dismissKey && data.dismissedUpsells?.includes(dismissKey)) return null;

  const basis = isDirectBasis(factor.cnCode) ? factor.seeDirect || 0 : totalSee(factor);
  const sefa = estimatedSefa(factor);
  if (sefa == null || basis <= 0) return null;

  const costNow = basis * DEFAULT_CERTIFICATE_PRICE;
  const costWithSefa = Math.max(0, basis - sefa) * DEFAULT_CERTIFICATE_PRICE;
  const saving = costNow - costWithSefa;
  if (saving <= 0) return null;
  const savingPct = Math.round((saving / costNow) * 100);

  return (
    <div className="relative rounded-2xl border border-primaryColor/25 bg-white overflow-hidden">
      <div className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800 m-0">
            {title ||
              t('sefaSavingsCard.defaultTitle', 'What SEFA would do for {{cnGood}}', {
                cnGood: cnGoodLabel(factor.cnCode),
              })}
          </p>
          <p className="text-[11px] text-slate-500 m-0 mt-0.5">
            {t(
              'sefaSavingsCard.basedOnDataNote',
              'Based on the data you just entered, at €{{price}}/tCO₂e certificate price.',
              { price: DEFAULT_CERTIFICATE_PRICE },
            )}
          </p>
        </div>
        {dismissKey && (
          <button
            type="button"
            onClick={() => dismissUpsell(dismissKey)}
            className="shrink-0 w-5 h-5 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center"
            title={t('sefaSavingsCard.dismissTitle', 'Dismiss')}
          >
            <FontAwesomeIcon
              icon={faXmark}
              className="text-[10px]"
            />
          </button>
        )}
      </div>

      {/* Comparison cards — CbamCostEstimator structure */}
      <div className="grid grid-cols-2 gap-3 px-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 m-0">
            {t('sefaSavingsCard.todayNoSefa', 'Today — no SEFA')}
          </p>
          <p className="text-lg font-bold text-slate-800 m-0 mt-0.5">
            {formatCurrency(costNow)}
            <span className="text-[10px] font-normal text-slate-400"> /t</span>
          </p>
          <p className="text-[10px] text-slate-400 m-0">
            {t('sefaSavingsCard.chargeableFullBasis', 'Chargeable {{basis}} tCO₂e/t (full basis)', {
              basis: toFixedNonZero(basis, 4),
            })}
          </p>
        </div>
        <div className="rounded-xl border border-primaryColor/30 bg-primaryColor/5 p-3">
          <p className="text-[9px] font-bold uppercase tracking-wider text-primaryColor m-0 flex items-center gap-1">
            <FontAwesomeIcon
              icon={faLock}
              className="text-[8px]"
            />
            {t('sefaSavingsCard.withSefaPremium', 'With SEFA — Premium')}
          </p>
          <p className="text-lg font-bold text-primaryColor m-0 mt-0.5">
            ~{formatCurrency(costWithSefa)}
            <span className="text-[10px] font-normal text-slate-400"> /t</span>
          </p>
          <p className="text-[10px] text-slate-400 m-0">
            {t(
              'sefaSavingsCard.chargeableWithSefa',
              'Chargeable {{net}} tCO₂e/t (basis − SEFA {{sefa}})',
              { net: toFixedNonZero(basis - sefa, 4), sefa: toFixedNonZero(sefa, 4) },
            )}
          </p>
        </div>
      </div>

      {/* Savings strip */}
      <div className="mt-3 px-4 py-2.5 bg-emerald-50 border-t border-emerald-100 flex items-center gap-3">
        <FontAwesomeIcon
          icon={faArrowTrendDown}
          className="text-emerald-600"
        />
        <p className="text-xs text-emerald-800 m-0 flex-1">
          {t('sefaSavingsCard.costDropsPrefix', "Your customers' CBAM cost drops by")}{' '}
          <span className="font-bold">
            {formatCurrency(saving)}/t ({savingPct}%)
          </span>{' '}
          {t('sefaSavingsCard.costDropsSuffix', '— a real reason to buy from you.')}
        </p>
        <CgRadixButton
          variant="primary"
          size="sm"
          onClick={() => openUpgrade('sefa')}
        >
          {t('sefaSavingsCard.calculateMySefaCta', 'Calculate my SEFA')}
          <FontAwesomeIcon
            icon={faArrowRight}
            className="text-[10px]"
          />
        </CgRadixButton>
      </div>
    </div>
  );
}
